import qs from 'qs';

import { API_URL } from '@/config/env';
import { conformanceApi } from '@/lib/api';
import type {
  Checkpoint,
  CheckpointsStatus,
  Evaluation,
  EvaluationStatusResponse,
  IObject,
  TriggerEvaluationRequest,
  UserRoleAccess,
} from '@/types';
import type {
  ApplicationConfiguration,
  CheckpointParametersRequest,
  EvaluationsResponse,
} from '@/modules/application/types';
import { getToken } from '@/lib/utils';
import { conformanceRequest } from '@/config/msalConfig';

export const fetchEvaluations = (filters: IObject) => {
  return conformanceApi.get<EvaluationsResponse>(`${API_URL}/evaluations`, {
    params: { ...filters },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'repeat', encode: false });
    },
  });
};

export const fetchEvaluationExcel = async (
  evaluationId: string,
  view: 'default' | 'dr' = 'default',
) => {
  const token = await getToken(conformanceRequest);
  return fetch(
    `${API_URL}/jobs/results/${evaluationId}?${new URLSearchParams({
      Format: 'Excel',
      view,
    })}`,
    {
      method: 'GET',
      headers: {
        authorization: `Bearer ${token}`,
        accept:
          'application/json, application/vnd.openxmlformats-officedocument.spreadsheet, text/plain, */*',
      },
    },
  );
};

export const fetchCheckpointAssessment = (
  recordId: string,
  checkpointId: string,
) => {
  return conformanceApi.get<{
    data: IObject[];
  }>(`${API_URL}/evaluations/${recordId}/assessments/${checkpointId}`);
};

export const fetchApplicationConfigurations = async (bitId: string) => {
  const response = await conformanceApi.get<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations`);

  return response.data.data;
};

export const triggerEvaluation = ({
  bitId,
  ...rest
}: TriggerEvaluationRequest) => {
  return conformanceApi.post<{ jobId: string; statusUrl: string }>(
    `${API_URL}/jobs/${bitId}`,
    rest,
    {
      headers: {
        'x-orr-automation-platform': 'ORR Portal',
      },
    },
  );
};

export const getEvaluationJobStatus = (jobId: string) => {
  return conformanceApi.get<EvaluationStatusResponse>(
    `${API_URL}/jobs/${jobId}/status`,
  );
};

export const getUserRoleAccess = () => {
  return conformanceApi.get<UserRoleAccess>(
    `${API_URL.replace('/orr', '')}/system/user`,
  );
};

export const getCheckpoints = () => {
  return conformanceApi.get<{
    message: string;
    status: string;
    data: Checkpoint[];
  }>(`${API_URL}/checkpoints`);
};

export const getCheckpointsStatus = () => {
  const url = `${API_URL.replace('/orr', '')}/system`;
  return conformanceApi.get<CheckpointsStatus>(url);
};

export const updateCheckpointStatus = (data: {
  checkpointId: string;
  preview: boolean;
  comment: string;
}) => {
  return conformanceApi.put(
    `${API_URL.replace('/orr', '')}/system/checkpoint/preview/`,
    data,
  );
};

export const createApplicationConfiguration = ({
  bitId,
  configurationId,
}: {
  bitId: string;
  configurationId?: string;
}) => {
  return conformanceApi.post<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations`, null, {
    params: { SourceConfigurationId: configurationId },
  });
};

export const updateCheckpointParameters = ({
  bitId,
  configurationId,
  checkpointId,
  parameters,
}: CheckpointParametersRequest) => {
  return conformanceApi.post<ApplicationConfiguration>(
    `${API_URL}/${bitId}/configurations/${configurationId}/checkpoints/${checkpointId}/parameters`,
    parameters,
  );
};

export const saveApplicationConfiguration = ({
  bitId,
  configurationId,
}: {
  bitId: string;
  configurationId: string;
}) => {
  return conformanceApi.put<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations/${configurationId}`, {
    isDraft: false,
  });
};

export const deleteApplicationConfiguration = ({
  bitId,
  configurationId,
}: {
  bitId: string;
  configurationId: string;
}) => {
  return conformanceApi.delete<{
    data: ApplicationConfiguration[];
    message: string;
    status: string;
  }>(`${API_URL}/${bitId}/configurations/${configurationId}`);
};

export const fetchEvaluation = async ({
  evaluationId,
  view,
}: {
  evaluationId: string;
  view: 'default' | 'dr';
}) => {
  const response = await conformanceApi.get<Evaluation>(
    `${API_URL}/evaluations/${evaluationId}`,
    {
      params: { Format: 'Json', view },
    },
  );
if (response.data && response.data.extraSourceCloudAccounts) {
  const hasAccounts = response.data.extraSourceCloudAccounts.length > 0;
  
  response.data.isWKTenant = hasAccounts && response.data.extraSourceCloudAccounts.every(acc => 
    acc.parentCloudAccountId === '8ac76c91-e7f1-41ff-a89c-3553b2da2c17' || 
    acc.parentCloudAccountId === '297538631377'
  );
}
  return response;

};
